import { FC } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { GroupWrap } from 'src/components/group-wrap';
import { ActiveSection } from 'src/components/layout/header/types';
import { ExternalLink, Link } from 'src/components/navigation/link';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import {
  EnhancedAuthenticatedMonolithUser,
  useOptionalAuthenticatedMonolithUser,
} from 'src/hooks/use-monolith-user';
import { isLng } from 'src/utils/is-lng';

const angularRoutes = ['#/storage'];

export const isStorageActive = (
  pathname: string,
  hash: string,
  auth: EnhancedAuthenticatedMonolithUser | null
) =>
  // for SSOs some paths live in the storage section
  (auth?.isSso &&
    !isLng(auth) &&
    Boolean(
      matchPath(pathname, { path: '/shippers' }) ||
        matchPath(pathname, { path: '/dashboard' }) ||
        matchPath(pathname, { path: '/settings/registration' }) ||
        matchPath(pathname, { path: '/storage' })
    )) ||
  Boolean(matchPath(pathname, { path: '/storage' })) ||
  (pathname.startsWith('/platform') &&
    angularRoutes.some((angularRoute) => hash.startsWith(angularRoute)));

export const SecondaryNavStorage: FC<{ activeSection: ActiveSection }> = ({
  activeSection,
}) => {
  const { hash, pathname } = useLocation();
  const monolithUser = useOptionalAuthenticatedMonolithUser();
  const { minTablet } = useBreakpoints();

  if (!activeSection.storage) return null;

  return (
    <GroupWrap
      gap={2}
      data-testid="secondary-nav"
      alignItems={!minTablet ? 'stretch' : 'center'}
      flexDirection={!minTablet ? 'column' : 'row'}
    >
      {monolithUser?.isShipper && (
        <>
          <Link
            mode="secondary"
            to="/storage/offers"
            isActive={
              pathname.startsWith('/storage/offers') ||
              (pathname.startsWith('/platform') &&
                hash.startsWith('#/storage/offers'))
            }
          >
            Auctions
          </Link>

          <ExternalLink
            mode="secondary"
            href={`${PRISMA_CONFIG.oldFrontendUrl}/#/storage/booking`}
          >
            FCFS Booking
          </ExternalLink>
        </>
      )}

      {monolithUser?.isSso && (
        <>
          <Link
            mode="secondary"
            to="/storage/offers"
            isActive={
              pathname.startsWith('/storage/offers') ||
              (pathname.startsWith('/platform') &&
                hash.startsWith('#/storage/offers') &&
                !hash.startsWith('#/storage/offers/create'))
            }
          >
            Manage Offer
          </Link>

          <ExternalLink
            mode="secondary"
            href={`${PRISMA_CONFIG.oldFrontendUrl}/#/storage/offers/create`}
            isActive={
              pathname.startsWith('/platform') &&
              hash.startsWith('#/storage/offers/create')
            }
          >
            Create Offer
          </ExternalLink>

          <Link
            mode="secondary"
            to="/shippers"
            isActive={
              pathname.startsWith('/shippers') ||
              pathname.startsWith('/dashboard/shippers')
            }
          >
            Shippers
          </Link>

          <Link
            mode="secondary"
            to="/storage/locations"
            isActive={
              pathname.startsWith('/storage/locations') ||
              (pathname.startsWith('/platform') &&
                hash.startsWith('#/storage/locations'))
            }
          >
            Storage Locations
          </Link>
        </>
      )}
    </GroupWrap>
  );
};
